const BUNNY_PLAYER_BASE_URL = "https://player.mediadelivery.net";
const BUNNY_PLAYER_HOSTS = ["player.mediadelivery.net", "iframe.mediadelivery.net"];
const BUNNY_MEDIA_SELECTOR = "bunny-stream-video-ads, bunny-stream-video, video";
const BUNNY_CONTROLS_SELECTOR = [
  "media-control-bar",
  "bunny-media-control-bar",
  "media-play-button.large-play",
  "bunny-settings-menu",
].join(", ");
const HIDE_BUNNY_CONTROLS_CSS = `
  media-controller.has-controls::after,
  media-controller.has-full-controls::after,
  media-control-bar,
  bunny-media-control-bar,
  media-play-button.large-play,
  bunny-settings-menu,
  bunny-settings-menu-button,
  media-settings-menu-button,
  media-fullscreen-button,
  media-pip-button,
  media-cast-button,
  media-airplay-button,
  bunny-media-time-range,
  bunny-time-display,
  media-mute-button,
  media-volume-range {
    display: none !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  bunny-stream-video,
  video {
    pointer-events: none !important;
  }
`;

export const BUNNY_VIDEO_IFRAME_ID = "video-container";

const toBooleanParam = (value) => {
  if (value === undefined || value === null) {
    return value;
  }
  return value ? "true" : "false";
};

const buildQueryParams = (options = {}) => ({
  autoplay: toBooleanParam(options.autoplay),
  controls: toBooleanParam(options.controls),
  loop: toBooleanParam(options.loop),
  muted: toBooleanParam(options.muted),
  preload: toBooleanParam(options.preload),
  responsive: toBooleanParam(options.responsive),
  rememberPosition: toBooleanParam(options.rememberPosition),
  playsinline: toBooleanParam(options.playsinline),
  chromecast: toBooleanParam(options.chromecast),
  disableAirplay: toBooleanParam(options.disableAirplay),
  disableIosPlayer: toBooleanParam(options.disableIosPlayer),
  showHeatmap: toBooleanParam(options.showHeatmap),
  showSpeed: toBooleanParam(options.showSpeed),
  compactControls: toBooleanParam(options.compactControls),
});

const parseUrl = (url) => {
  try {
    return new URL(url, window.location.origin);
  } catch (error) {
    return null;
  }
};

export const buildBunnyEmbedUrl = (videoUrl, options = {}) => {
  if (!videoUrl) {
    return "";
  }

  const url = parseUrl(videoUrl);
  if (!url) {
    return videoUrl;
  }

  const segments = url.pathname.split("/").filter(Boolean);
  if (BUNNY_PLAYER_HOSTS.includes(url.hostname) && ["embed", "play"].includes(segments[0]) && segments.length >= 3) {
    url.protocol = "https:";
    url.hostname = "player.mediadelivery.net";
    url.pathname = `/embed/${segments[1]}/${segments[2]}`;
  }

  Object.entries(buildQueryParams(options)).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
};

const addBaseTag = (embedDocument) => {
  const base = embedDocument.createElement("base");
  base.href = BUNNY_PLAYER_BASE_URL;
  embedDocument.head.prepend(base);
};

const addHideControlsStyle = (embedDocument) => {
  const style = embedDocument.createElement("style");
  style.textContent = HIDE_BUNNY_CONTROLS_CSS;
  embedDocument.head.appendChild(style);
};

const removeControls = (embedDocument) => {
  embedDocument.querySelectorAll(BUNNY_CONTROLS_SELECTOR).forEach((element) => element.remove());
};

const buildBunnyEmbedDocument = (html) => {
  const embedDocument = new DOMParser().parseFromString(html, "text/html");
  addBaseTag(embedDocument);
  addHideControlsStyle(embedDocument);
  removeControls(embedDocument);

  return `<!DOCTYPE html>${embedDocument.documentElement.outerHTML}`;
};

class BunnyStreamPlayer {
  constructor(elementId, options = {}) {
    this.elementId = elementId;
    this.options = {
      autoplay: false,
      controls: false,
      loop: false,
      muted: false,
      preload: true,
      responsive: false,
      rememberPosition: false,
      playsinline: true,
      ...options,
    };
    this.videoUrl = buildBunnyEmbedUrl(options.url, this.options);
    this.eventHandlers = {};
    this.boundEventHandlers = {};
    this.cuePointHandlers = new Set();
    this.cuePoints = [];
    this.destroyed = false;
    this.readyPromise = this.initialize();
  }

  async initialize() {
    if (this.destroyed) {
      return null;
    }

    this.iframe = this.getIframe();
    await this.loadEmbedDocument();
    this.media = await this.waitForMediaElement();
    this.media.addEventListener("timeupdate", this.handleCuePointTimeUpdate);
    return this.media;
  }

  getIframe() {
    const element = document.getElementById(this.elementId);

    if (!element) {
      throw new Error(`Video element #${this.elementId} was not found`);
    }

    const iframe =
      element.tagName.toLowerCase() === "iframe"
        ? element
        : element.querySelector("iframe") || this.createIframe(element);

    iframe.removeAttribute("srcdoc");
    iframe.setAttribute("src", "about:blank");
    iframe.setAttribute("loading", "lazy");
    iframe.setAttribute("tabindex", "-1");
    iframe.setAttribute("allow", "accelerometer; gyroscope; autoplay; encrypted-media;");
    iframe.removeAttribute("allowfullscreen");
    iframe.style.border = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.position = "absolute";
    iframe.style.top = "0";
    iframe.style.left = "0";
    iframe.style.width = "100%";
    iframe.style.height = "100%";

    return iframe;
  }

  async loadEmbedDocument() {
    const response = await fetch(this.videoUrl);

    if (!response.ok) {
      throw new Error(`Could not load Bunny embed document: ${response.status}`);
    }

    const html = await response.text();
    const srcDoc = buildBunnyEmbedDocument(html);

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error("Timed out while loading Bunny embed document")), 10000);
      this.iframe.onload = () => {
        clearTimeout(timeout);
        resolve();
      };
      this.iframe.srcdoc = srcDoc;
    });
  }

  async waitForMediaElement() {
    let attempts = 0;
    while (!this.destroyed && attempts < 200) {
      const media = this.getMediaElement();

      if (media && typeof media.play === "function" && typeof media.pause === "function") {
        return media;
      }

      attempts += 1;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    throw new Error("Bunny media element was not found");
  }

  getMediaElement() {
    const iframeDocument = this.iframe && (this.iframe.contentDocument || this.iframe.contentWindow?.document);
    if (!iframeDocument) {
      return null;
    }

    const media = iframeDocument.querySelector(BUNNY_MEDIA_SELECTOR);
    return media?.shadowRoot?.querySelector("video") || media;
  }

  createIframe(container) {
    const iframe = document.createElement("iframe");
    iframe.id = `${this.elementId}-iframe`;
    container.innerHTML = "";
    container.appendChild(iframe);
    return iframe;
  }

  ready() {
    return this.readyPromise;
  }

  isCurrentVideo(videoUrl) {
    return this.videoUrl === buildBunnyEmbedUrl(videoUrl, this.options);
  }

  on(event, handler) {
    if (event === "cuepoint") {
      this.cuePointHandlers.add(handler);
      return this;
    }

    this.eventHandlers[event] = this.eventHandlers[event] || new Set();
    this.eventHandlers[event].add(handler);

    this.readyPromise.then(() => {
      if (!this.destroyed && this.eventHandlers[event] && this.eventHandlers[event].has(handler)) {
        this.addMediaEventHandler(event, handler);
      }
    });

    return this;
  }

  off(event, handler) {
    if (event === "cuepoint") {
      if (handler) {
        this.cuePointHandlers.delete(handler);
      } else {
        this.cuePointHandlers.clear();
      }
      return this;
    }

    const handlers = this.eventHandlers[event];
    if (!handlers) {
      return this;
    }

    const handlersToRemove = handler ? [handler] : Array.from(handlers);
    handlersToRemove.forEach((eventHandler) => {
      handlers.delete(eventHandler);
      if (this.media && this.boundEventHandlers[event] && this.boundEventHandlers[event].has(eventHandler)) {
        this.media.removeEventListener(this.getMediaEventName(event), this.boundEventHandlers[event].get(eventHandler));
        this.boundEventHandlers[event].delete(eventHandler);
      }
    });

    return this;
  }

  addCuePoint(time, data = {}) {
    const cuePoint = {
      id: `${Date.now()}-${this.cuePoints.length}`,
      time,
      data,
      triggered: false,
    };

    this.cuePoints.push(cuePoint);
    return Promise.resolve(cuePoint.id);
  }

  clearCuePoints() {
    this.cuePoints = [];
  }

  handleCuePointTimeUpdate = () => {
    const seconds = this.media?.currentTime || 0;
    this.cuePoints
      .filter((cuePoint) => !cuePoint.triggered && seconds >= cuePoint.time)
      .forEach((cuePoint) => {
        cuePoint.triggered = true;
        this.cuePointHandlers.forEach((handler) =>
          handler({
            id: cuePoint.id,
            time: cuePoint.time,
            data: cuePoint.data,
          })
        );
      });
  };

  getMediaEventName(event) {
    if (event === "bufferstart") {
      return "waiting";
    }
    if (event === "bufferend") {
      return "canplay";
    }
    return event;
  }

  getBufferedPercent() {
    const duration = this.media?.duration || 0;
    const buffered = this.media?.buffered;

    if (!duration || !buffered || !buffered.length) {
      return 0;
    }

    return buffered.end(buffered.length - 1) / duration;
  }

  buildEventData(event) {
    if (event === "timeupdate") {
      return {
        duration: this.media.duration || 0,
        seconds: this.media.currentTime || 0,
      };
    }

    if (event === "progress") {
      return {
        percent: this.getBufferedPercent(),
      };
    }

    if (event === "error") {
      return {
        name: "MediaError",
        method: "play",
        message: this.media.error?.message || "Could not play Bunny video",
      };
    }

    return {};
  }

  addMediaEventHandler(event, handler) {
    const mediaEvent = this.getMediaEventName(event);

    if (
      !this.media ||
      !["canplay", "ended", "error", "play", "progress", "timeupdate", "waiting"].includes(mediaEvent)
    ) {
      return;
    }

    this.boundEventHandlers[event] = this.boundEventHandlers[event] || new Map();

    if (this.boundEventHandlers[event].has(handler)) {
      return;
    }

    const eventHandler = () => handler(this.buildEventData(event));
    this.boundEventHandlers[event].set(handler, eventHandler);
    this.media.addEventListener(mediaEvent, eventHandler);
  }

  callMethod(method, ...args) {
    return this.readyPromise.then(() => {
      if (!this.media || typeof this.media[method] !== "function") {
        throw new Error(`Bunny player method ${method} is not available`);
      }

      return this.media[method](...args);
    });
  }

  callGetter(prop) {
    return this.readyPromise.then(() => {
      if (!this.media || this.media[prop] === undefined) {
        throw new Error(`Bunny player property ${prop} is not available`);
      }

      return this.media[prop];
    });
  }

  play() {
    return this.callMethod("play");
  }

  pause() {
    return this.callMethod("pause");
  }

  setCurrentTime(time) {
    return this.readyPromise.then(() => {
      return new Promise((resolve) => {
        const cleanup = () => {
          clearTimeout(timeout);
          this.media.removeEventListener("seeked", handleSeeked);
        };
        const handleSeeked = () => {
          cleanup();
          resolve();
        };
        const timeout = setTimeout(handleSeeked, 1000);

        this.media.addEventListener("seeked", handleSeeked);
        this.media.currentTime = time;

        if (!this.media.seeking && Math.abs((this.media.currentTime || 0) - time) <= 0.1) {
          handleSeeked();
        }
      });
    });
  }

  getCurrentTime() {
    return this.callGetter("currentTime");
  }

  getDuration() {
    return this.callGetter("duration");
  }

  setMuted(muted) {
    return this.readyPromise.then(() => {
      this.media.muted = muted;
    });
  }

  setVolume(volume) {
    return this.readyPromise.then(() => {
      this.media.volume = volume <= 1 ? volume : volume / 100;
    });
  }

  destroy() {
    this.destroyed = true;
    Object.keys(this.eventHandlers).forEach((event) => this.off(event));
    this.cuePointHandlers.clear();
    this.cuePoints = [];

    if (this.media) {
      this.media.removeEventListener("timeupdate", this.handleCuePointTimeUpdate);
    }

    if (this.iframe) {
      this.iframe.removeAttribute("srcdoc");
    }

    return Promise.resolve();
  }
}

export default BunnyStreamPlayer;
